import * as React from 'react';
import { useCallback, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { ReactFlow, Background, BackgroundVariant, ConnectionMode, Controls, type Edge, type Node, type ColorMode, type OnConnect, type OnNodesChange, type OnEdgesChange, type OnConnectStart, type OnConnectEnd, type ReactFlowInstance } from '@xyflow/react';
import { useTheme } from '@app/providers/ThemeProvider';
// import { useFlowSelection } from '@/context/flow-selection';
import env from '@shared/api/env';
import { getSupabaseAuthHeaders } from '@shared/api/supabase-auth-headers';
import { useWorkspace } from '@/context/workspace';
import { adaptFlowGraph } from '@flows/lib/flow-graph-adapter';
// import { layoutWithElk } from '@flows/lib/layout-elk';
import { useCanvasStore } from '@features/flows/state/canvas-store';
import { CommandBar } from '@features/flows/palette/CommandBar';
import AutocastMapperDrawer from '@features/flows/components/edge/AutocastMapperDrawer';
import { parseHandleId } from '@features/flows/utils/edge';
import nodeTypes from '@flows/canvas/nodeTypes';
import DataEdge from '@flows/canvas/edgeTypes/DataEdge';
import ExecEdge from '@flows/canvas/edgeTypes/ExecEdge';
import FlowHeader from '@flows/ui/FlowHeader';
// import { useNodeSelection } from '@/context/node-selection';
// import ContextMenu from '@flows/ui/ContextMenu';
import '@flows/canvas/colors.css';
import '@xyflow/react/dist/style.css';
import { PanelGroup, Panel, PanelResizeHandle, type ImperativePanelHandle } from 'react-resizable-panels';
import { FlowChatPopover } from '@features/chat/flow-chat/FlowChatPopover';
import { EloraChatPopover } from '@features/chat/elora-chat/EloraChatPopover';
import NodeConfigPanel from '@features/flows/components/NodeConfigPanel';
import { loadRegistry, type NodeTemplate } from '@features/flows/palette/registry';
import { useRuntimeStore } from '@features/flows/runtime/useRuntimeStore';
import NodeInspectorPanel from '@features/flows/components/output/NodeInspectorPanel';
import type { BindingSpec } from '@features/flows/utils/types';

// Production canvas. Dev canvas retired — this is the single canonical canvas.
export default function FlowCanvasPage() {
  const { theme } = useTheme();
  const rfColorMode: ColorMode = theme === 'dark' ? 'dark' : theme === 'light' ? 'light' : (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light');
  const { flowId: urlFlowId } = useParams<{ flowId?: string }>();
  const { active: ws } = useWorkspace();
  const [isLoadingFlow, setLoading] = React.useState(false);
  const [flowError, setFlowError] = React.useState<string | null>(null);
  const nodes = useCanvasStore((s) => s.graph.nodes);
  const edges = useCanvasStore((s) => s.graph.edges);
  const selection = useCanvasStore((s) => s.ui.selection);
  const replaceGraph = useCanvasStore((s) => s.replaceGraph);
  const connectEdge = useCanvasStore((s) => s.connectEdge);
  const beginConnect = useCanvasStore((s) => s.beginConnect);
  const finishConnectAt = useCanvasStore((s) => s.finishConnectAt);
  const setNodeAttrs = useCanvasStore((s) => s.setNodeAttrs);
  const setNodeLabel = useCanvasStore((s) => s.setNodeLabel);
  const toggleAttrPin = useCanvasStore((s) => s.toggleAttrPin);
  const setBindingSpec = useCanvasStore((s) => s.setBindingSpec);
  const setTemplates = useCanvasStore((s) => s.setTemplates);
  const markDraggingNodes = useCanvasStore((s) => s.markDraggingNodes);
  const [flowName, setFlowName] = React.useState<string | null>(null);
  const [flowRevision, setFlowRevision] = React.useState<number | null>(null);
  const [rf, setRf] = React.useState<ReactFlowInstance<Node, Edge> | null>(null);
  const ui = useCanvasStore((s) => s.ui);
  const openCommandBarAt = useCanvasStore((s) => s.openCommandBarAt);
  const consumePendingCenter = useCanvasStore((s) => s.consumePendingCenter);
  const structureVersion = useCanvasStore((s) => s.graph.structureVersion);
  const containerRef = React.useRef<HTMLDivElement>(null);
  // const { selectNode: selectCtxNode } = useNodeSelection();
  const rightPanelRef = React.useRef<ImperativePanelHandle | null>(null);
  const [registry, setRegistry] = React.useState<Map<string, NodeTemplate> | null>(null);
  const [configPanelOpen, setConfigPanelOpen] = React.useState<boolean>(false);
  const [configPanelSize, setConfigPanelSize] = React.useState<number>(() => {
    try {
      const stored = localStorage.getItem('flows.configPanel.size');
      return stored ? Number(stored) : 30; // %
    } catch {
      return 30;
    }
  });
  const configPanelSizeRef = React.useRef(configPanelSize);
  const setGraph = useRuntimeStore((s) => s.setGraph);

  const pendingCenterNodeId = useCanvasStore((s) => s.ui.pendingCenterNodeId);

  const openCommandBarCentered = React.useCallback((context: { mode: 'pane' | 'connect'; anchorX?: number; anchorY?: number }) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (rect) {
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      openCommandBarAt(cx, cy, { ...context, centered: true });
    }
  }, [openCommandBarAt]);

  // No default demo graph; start with an empty canvas for new flows

  // Load palette registry for template lookup
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const map = await loadRegistry();
        if (!cancelled) setRegistry(map);
        if (!cancelled) setTemplates(map);
      } catch (e) {
        console.warn('loadRegistry failed', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Fetch flow directly from PostgREST for a clean slate
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!urlFlowId) return;
      // Ensure palette registry is loaded so adapter can resolve templates
      if (!registry) return;
      setLoading(true);
      setFlowError(null);
      try {
        // Timeout session/header lookup quickly to avoid indefinite spinners
        const headers = await getSupabaseAuthHeaders();
        const withWs = ws?.id ? { ...headers, 'X-Workspace-ID': ws.id } : headers;
        const base = env.PGR_BASE.replace(/\/$/, '');
        const params = new URLSearchParams();
        params.set('id', `eq.${urlFlowId}`);
        params.set('select', 'id,name,description,revision,graph,updated_at');
        const url = `${base}/flows?${params.toString()}`;

        // Abort fetch after a short timeout; avoid hanging UI
        const ctrl = new AbortController();
        const timeout = setTimeout(() => {
          try { ctrl.abort(); } catch {}
        }, 7000);
        const res = await fetch(url, { headers: withWs, signal: ctrl.signal });
        clearTimeout(timeout);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const rows = (await res.json()) as any[];
        const row = rows?.[0];
        if (!row) throw new Error('Flow not found');
        if (cancelled) return;
        const hasGraph = row.graph && Array.isArray(row.graph.nodes) && row.graph.nodes.length > 0;
        if (hasGraph) {
          const { nodes: rfNodes, edges: rfEdges } = adaptFlowGraph(row.graph);
          replaceGraph({ nodes: rfNodes as any, edges: rfEdges as any }, { id: row.id, name: row.name ?? null, revision: row.revision ?? 0 });
          setFlowName(row.name ?? null);
          setFlowRevision(row.revision ?? 0);
        } else {
          replaceGraph({ nodes: [], edges: [] }, { id: row.id, name: row.name ?? null, revision: row.revision ?? 0 });
          setFlowName(row.name ?? null);
          setFlowRevision(row.revision ?? 0);
        }
      } catch (e: any) {
        if (!cancelled) {
          const msg = (e && (e.name === 'AbortError' || String(e).includes('AbortError'))) ? 'Request timed out' : (e?.message ?? String(e));
          setFlowError(msg);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [urlFlowId, ws?.id, registry]);

  // hydrate graph when flow loads
  // hydration via PostgREST fetch above

  // autolayout temporarily disabled to avoid mount-time update loops

  // keybindings disabled until edit handlers are re-enabled safely

  // map edges to custom types without mutating store
  const rfEdges = useMemo<Edge[]>(() =>
    edges.map((e) => {
      const looksData =
        (typeof (e as any).sourceHandle === 'string' && String((e as any).sourceHandle).startsWith('data:')) ||
        (typeof (e as any).targetHandle === 'string' && String((e as any).targetHandle).startsWith('data:'));
      const channel: 'data' | 'exec' = ((e as any).data?.channel ?? (looksData ? 'data' : 'exec')) as any;
      return { ...e, type: channel === 'data' ? 'data' : 'exec' } as Edge;
    }),
  [edges]);

  const onConnect: OnConnect = useCallback((params) => {
    connectEdge(params as any);
  }, [connectEdge]);

  const onConnectStart: OnConnectStart = useCallback((_, params) => {
    const parsed = parseHandleId(params.handleId as string);
    if (!parsed) return;
    beginConnect(params.nodeId!, parsed.raw, parsed.channel, parsed.io);
  }, [beginConnect]);

  const onConnectEnd: OnConnectEnd = useCallback((evt) => {
    const target = (evt as any)?.target as HTMLElement | null;
    if (!target) return;
    // Only open the command bar when the drag ends on the empty pane itself
    if (!target.classList.contains('react-flow__pane')) return;
    const isMouseEvent = (e: any): e is MouseEvent => typeof e?.clientX === 'number' && typeof e?.clientY === 'number';
    const { x, y } = isMouseEvent(evt)
      ? { x: (evt as MouseEvent).clientX, y: (evt as MouseEvent).clientY }
      : {
          x: (evt as TouchEvent).changedTouches[0]?.clientX ?? 0,
          y: (evt as TouchEvent).changedTouches[0]?.clientY ?? 0,
        };
    finishConnectAt(x, y);
  }, [finishConnectAt]);

  const onNodesChange: OnNodesChange = useCallback((changes) => { useCanvasStore.getState().applyNodesChange(changes); }, []);
  const onNodeDragStart = useCallback(() => { markDraggingNodes(true); }, [markDraggingNodes]);
  const onNodeDragStop = useCallback(() => { markDraggingNodes(false); }, [markDraggingNodes]);

  const onEdgesChange: OnEdgesChange = useCallback((changes) => { useCanvasStore.getState().applyEdgesChange(changes); }, []);

  // Track active node for the right-side config panel
  useEffect(() => {
    const nids = Array.from(selection.nodes);
    if (nids.length === 1) setConfigPanelOpen(true);
    else if (nids.length === 0) setConfigPanelOpen(false);
  }, [selection.nodes]);

  const activeNodeId = useMemo(() => {
    const nids = Array.from(selection.nodes);
    return nids[0] ?? null;
  }, [selection.nodes]);

  const onChangeAttrs = useCallback((nodeId: string, next: any) => setNodeAttrs(nodeId, next), [setNodeAttrs]);

  const onTogglePin = useCallback((nodeId: string, field: string) => toggleAttrPin(nodeId, field), [toggleAttrPin]);

  const onChangeBinding = useCallback((nodeId: string, field: string, spec: Partial<BindingSpec>) => setBindingSpec(nodeId, field, spec), [setBindingSpec]);

  const onChangeLabel = useCallback((nodeId: string, next: string) => setNodeLabel(nodeId, next), [setNodeLabel]);

  // Imperatively expand/collapse right panel based on selection
  useEffect(() => {
    configPanelSizeRef.current = configPanelSize;
  }, [configPanelSize]);

  const prevPanelStateRef = React.useRef<{ open: boolean; size: number }>({ open: configPanelOpen, size: configPanelSize });

  useEffect(() => {
    const panel = rightPanelRef.current;
    if (!panel) {
      prevPanelStateRef.current = { open: configPanelOpen, size: configPanelSize };
      return;
    }
    const prev = prevPanelStateRef.current;
    try {
      if (configPanelOpen !== prev.open) {
        if (configPanelOpen) {
          panel.expand();
          if (typeof (panel as any).getSize === 'function' && (panel as any).getSize() < 10) {
            (panel as any).setSize?.(configPanelSize || 30);
          }
        } else {
          panel.collapse();
        }
      } else if (configPanelOpen && Math.abs(configPanelSize - prev.size) > 0.5) {
        (panel as any).setSize?.(configPanelSize);
      }
    } catch {}
    prevPanelStateRef.current = { open: configPanelOpen, size: configPanelSize };
  }, [configPanelOpen, configPanelSize]);

  const handleGroupLayout = React.useCallback((sizes: number[]) => {
    const right = sizes[1];
    if (typeof right !== 'number') return;
    if (Math.abs(right - configPanelSizeRef.current) < 0.5) return;
    configPanelSizeRef.current = right;
    setConfigPanelSize(right);
    try { localStorage.setItem('flows.configPanel.size', String(right)); } catch {}
  }, []);

  const undo = useCanvasStore((s) => s.undo);
  const redo = useCanvasStore((s) => s.redo);
  const deleteSelection = useCanvasStore((s) => s.deleteSelection);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = document.activeElement as HTMLElement | null;
      const inInput = !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable);
      if (inInput) return;
      const key = e.key.toLowerCase();
      if ((e.metaKey || e.ctrlKey) && !e.shiftKey && key === 'z') {
        e.preventDefault();
        undo();
      } else if ((e.metaKey || e.ctrlKey) && e.shiftKey && key === 'z') {
        e.preventDefault();
        redo();
      } else if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        deleteSelection();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [undo, redo, deleteSelection]);

  const edgeTypes = useMemo(() => ({ data: DataEdge, exec: ExecEdge }), []);

  useEffect(() => {
    setGraph(nodes as any, edges as any);
    // We purposefully skip nodes/edges here to avoid re-syncing on drag-only updates.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [structureVersion, setGraph]);

  // Status/time/error are now derived inside CustomNode from runtime store

  const insertFromTemplate = useCallback((tpl: any) => {
    const anchor = (ui.commandBar.context && typeof ui.commandBar.context.anchorX === 'number' && typeof ui.commandBar.context.anchorY === 'number')
      ? { x: ui.commandBar.context.anchorX as number, y: ui.commandBar.context.anchorY as number }
      : { x: ui.commandBar.x, y: ui.commandBar.y };
    if (!rf) return;
    const p = (rf as any).screenToFlowPosition(anchor, { snapToGrid: true }) as { x: number; y: number };
    const pos = p;
    useCanvasStore.getState().insertNodeFromTemplate(tpl, pos);
    try { rf?.setCenter(pos.x, pos.y, { zoom: rf.getZoom?.() ?? 1, duration: 300 }); } catch {}
  }, [rf, ui.commandBar.x, ui.commandBar.y]);

  useEffect(() => {
    if (!rf) return;
    if (!pendingCenterNodeId) return;
    const nodeToCenter = nodes.find((n) => n.id === pendingCenterNodeId);
    if (!nodeToCenter) return;
    const id = consumePendingCenter();
    if (!id) return;
    try {
      if (typeof rf.fitView === 'function') {
        rf.fitView({ nodes: [nodeToCenter], duration: 300, padding: 0.6 });
      } else if (typeof rf.setCenter === 'function') {
        const width = nodeToCenter.width ?? 0;
        const height = nodeToCenter.height ?? 0;
        const baseX = nodeToCenter.position?.x ?? 0;
        const baseY = nodeToCenter.position?.y ?? 0;
        rf.setCenter(baseX + width / 2, baseY + height / 2, { duration: 300, zoom: rf.getZoom?.() ?? 1 });
      }
    } catch {/* noop */}
  }, [pendingCenterNodeId, nodes, rf, consumePendingCenter]);

  const handleGraphReplace = useCallback((graph: { nodes: Node[]; edges: Edge[] } | undefined, revision: number, name?: string | null) => {
    if (graph?.nodes && graph?.edges) {
      replaceGraph({ nodes: graph.nodes as any, edges: graph.edges as any }, { revision: revision ?? 0, name: name ?? null });
    }
    setFlowRevision(revision ?? 0);
    if (typeof name !== 'undefined') setFlowName(name ?? null);
  }, [replaceGraph, setFlowRevision, setFlowName]);

  return (
    <div className="w-full h-full relative">
      <FlowHeader
        flowId={urlFlowId}
        name={flowName}
        revision={flowRevision}
        onGraphReplace={handleGraphReplace}
      />
      <div
        ref={containerRef}
        className="absolute inset-0 top-12"
      >
        <PanelGroup
          direction="horizontal"
          className="h-full"
          onLayout={handleGroupLayout}
        >
          <Panel minSize={40} defaultSize={100 - configPanelSize}>
            {isLoadingFlow && <div className="p-4 text-sm">Loading flow…</div>}
            {!isLoadingFlow && flowError && (
              <div className="p-4 text-sm text-destructive">Error: {String(flowError)}</div>
            )}
            {!isLoadingFlow && !flowError && (
              <ReactFlow<Node, Edge>
                nodes={nodes}
                edges={rfEdges}
                nodeTypes={nodeTypes}
                edgeTypes={edgeTypes}
                colorMode={rfColorMode}
                connectionMode={ConnectionMode.Loose}
                // Always allow connections; autocast reconciles mismatched pins after the drop.
                isValidConnection={() => true}
                onInit={(inst) => setRf(inst)}
                onNodesChange={onNodesChange}
                onNodeDragStart={onNodeDragStart}
                onNodeDragStop={onNodeDragStop}
                onEdgesChange={onEdgesChange}
                onConnect={onConnect}
                onConnectStart={onConnectStart}
                onConnectEnd={onConnectEnd}
                onPaneContextMenu={(evt) => { evt.preventDefault(); openCommandBarCentered({ mode: 'pane', anchorX: evt.clientX, anchorY: evt.clientY }); }}
                onEdgeDoubleClick={(_, edge) => {
                  if (!edge?.id) return;
                  useCanvasStore.getState().openMapper(edge.id, 'liquid');
                }}
                onSelectionChange={(s: any) => {
                  useCanvasStore.getState().select({ nodes: (s?.nodes ?? []).map((n: Node) => n.id), edges: (s?.edges ?? []).map((e: Edge) => e.id) });
                }}
                panOnScroll
                panOnDrag
                zoomOnScroll
                snapToGrid
                snapGrid={[10, 10]}
                fitView
              >
                <Background variant={BackgroundVariant.Dots} gap={24} size={1} />
                <Controls position="bottom-left" showInteractive={false} />
              </ReactFlow>
            )}
            <CommandBar onSelect={insertFromTemplate} />
            <AutocastMapperDrawer />
          </Panel>
          <PanelResizeHandle className="w-2 bg-border hover:bg-accent transition-colors cursor-col-resize flex items-center justify-center">
            <div className="w-1 h-8 bg-muted-foreground/30 rounded-full" />
          </PanelResizeHandle>
          <Panel ref={rightPanelRef} minSize={20} defaultSize={configPanelSize} collapsible>
            <div className="h-full border-l">
              <NodeConfigPanel
                nodeId={activeNodeId}
                templateRegistry={registry}
                onChangeAttrs={onChangeAttrs}
                onChangeLabel={onChangeLabel}
                onTogglePin={onTogglePin}
                onChangeBinding={onChangeBinding}
              />
            </div>
          </Panel>
        </PanelGroup>
        {urlFlowId ? (
          <FlowChatPopover flowId={urlFlowId} workspaceId={ws?.id} />
        ) : null}
        {urlFlowId ? (
          <EloraChatPopover flowId={urlFlowId} workspaceId={ws?.id} />
        ) : null}
        {/* Centered inspector overlay (non-blocking backdrop) */}
        <div className="absolute inset-0 grid place-items-center pointer-events-none z-[60]">
          <NodeInspectorPanel
            mount="container"
            className="pointer-events-auto w-[1040px] h-[400px] max-h-none"
            pinsByNodeId={Object.fromEntries(nodes.map((n) => [n.id, (n.data as any)?.pins || {}]))}
            templatesByNodeId={Object.fromEntries(nodes.map((n) => {
              const key = (n.data as any)?.templateKey || (n.data as any)?.handler || '';
              const tpl = key && registry ? registry.get(String(key)) : undefined;
              return [n.id, tpl];
            }))}
          />
        </div>
      </div>
    </div>
  );
}
